import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { ListItemText, Menu, MenuItem } from '@mui/material';
import TableCard from 'features/IRWorkstation/components/Common/TableCard';
import FundraisingDrawer from 'features/IRWorkstation/components/Fundraising/FundraisingDrawer';
import SalesforceTaskCreateDrawer from 'features/IRWorkstation/components/Tasks/SalesforceTaskCreateDrawer';
import buildFundraisingGridColumns from 'features/IRWorkstation/components/Fundraising/fundraisingGridColumns';
import { FUNDRAISING_COLUMNS } from 'features/IRWorkstation/constants/fundraisingColumns';
import salesforceFundraisingsService from 'services/salesforceFundraisingsService';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

// The shared fundraising table card used by the Raise Dashboard "LP Pipeline"
// table and the LP360 "Fundraising opportunities" card: title bar, filter bar,
// grid (fixed column headers, server-side sort, infinite scroll), the row kebab
// menu (Edit / Create Task / Convert to Investor / View record in CRM), the edit
// drawer and the Create Task drawer.
//
// Callers own the data: pass the fetched rows plus the grid callbacks, the
// filter bar node, the column set (LP360 swaps the Account column for Entity),
// and onRefresh for whatever else should reload after a save or conversion.
const FundraisingTableCard = ({
	title,
	totalCount,
	filterBar = null,
	columnDefs = FUNDRAISING_COLUMNS,
	getAccountLink,
	rows,
	loading,
	loadingMore = false,
	hasMore = false,
	error = null,
	errorMessage = 'Unable to load Fundraising records',
	sortModel,
	onSortModelChange,
	onRowsScrollEnd,
	noRowsMessage,
	dataTestId = 'fundraisings-grid',
	cardTestId,
	onRefresh,
	onMenuAction,
}) => {
	const { tenant } = useIRWorkstation();
	const { showNotification } = useNotificationContext();

	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [selectedFundraising, setSelectedFundraising] = useState(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
	const menuOpen = Boolean(menuAnchorEl);

	const handleMenuOpen = useCallback((event, fundraising) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
		setSelectedFundraising(fundraising);
	}, []);

	const columns = useMemo(
		() =>
			buildFundraisingGridColumns(handleMenuOpen, getAccountLink, columnDefs),
		[handleMenuOpen, getAccountLink, columnDefs]
	);

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
		setSelectedFundraising(null);
	};

	const handleMenuEdit = () => {
		onMenuAction?.('edit');
		if (selectedFundraising) {
			setDrawerOpen(true);
		}
		// Close menu but don't reset selectedFundraising as data is needed for drawer
		setMenuAnchorEl(null);
	};

	const handleMenuCreateTask = () => {
		onMenuAction?.('createTask');
		if (selectedFundraising) {
			setTaskDrawerOpen(true);
		}
		// Close menu but don't reset selectedFundraising as data is needed for drawer
		setMenuAnchorEl(null);
	};

	const handleTaskDrawerClose = useCallback(() => {
		setTaskDrawerOpen(false);
		setSelectedFundraising(null);
	}, []);

	// The task is related to the Fundraising record itself (WhatId)
	const taskRelatedRecord = useMemo(
		() =>
			selectedFundraising && {
				id: selectedFundraising.id,
				name: selectedFundraising.name,
			},
		[selectedFundraising]
	);

	const handleConvert = async () => {
		onMenuAction?.('convertToInvestor');
		const fundraisingToConvert = selectedFundraising;
		handleMenuClose();

		if (!fundraisingToConvert) return;

		showNotification('Converting to Investor Commitment...', 'info');

		try {
			await salesforceFundraisingsService.convertFundraising(
				fundraisingToConvert.id
			);
			showNotification(
				'Fundraising converted to Investor Commitment',
				'success'
			);
			onRefresh?.();
		} catch (err) {
			showNotification(
				err?.message || 'Failed to convert fundraising',
				'error'
			);
		}
	};

	// The Lightning URL is built server-side (SalesforceUrlBuilderService) and
	// returned as salesforce_url, so the CRM URL format lives in one place.
	const handleViewInCrm = () => {
		onMenuAction?.('viewInCrm');
		const url = selectedFundraising?.salesforce_url;
		handleMenuClose();

		if (!url) {
			showNotification('Salesforce record URL is unavailable', 'error');
			return;
		}

		window.open(url, '_blank', 'noopener,noreferrer');
	};

	return (
		<>
			<TableCard
				title={title}
				totalCount={totalCount}
				totalLabel='total fundraisings'
				filterBar={filterBar}
				columns={columns}
				rows={rows}
				loading={loading}
				loadingMore={loadingMore}
				hasMore={hasMore}
				error={error}
				errorMessage={errorMessage}
				sortModel={sortModel}
				onSortModelChange={onSortModelChange}
				onRowsScrollEnd={onRowsScrollEnd}
				noRowsMessage={noRowsMessage}
				dataTestId={dataTestId}
				cardTestId={cardTestId}
			/>
			{selectedFundraising && drawerOpen && (
				<FundraisingDrawer
					open={drawerOpen}
					fundraising={selectedFundraising}
					tenant={tenant}
					onClose={() => {
						setDrawerOpen(false);
						setSelectedFundraising(null);
					}}
					onSaved={onRefresh}
				/>
			)}
			{taskRelatedRecord && taskDrawerOpen && (
				<SalesforceTaskCreateDrawer
					open={taskDrawerOpen}
					relatedRecord={taskRelatedRecord}
					onClose={handleTaskDrawerClose}
				/>
			)}
			<Menu
				anchorEl={menuAnchorEl}
				open={menuOpen}
				onClose={handleMenuClose}
				PaperProps={{
					elevation: 2,
					sx: {
						minWidth: 150,
						maxWidth: 200,
					},
				}}
			>
				<MenuItem onClick={handleMenuEdit}>
					<ListItemText>Edit</ListItemText>
				</MenuItem>
				<MenuItem onClick={handleMenuCreateTask}>
					<ListItemText>Create Task</ListItemText>
				</MenuItem>
				{selectedFundraising?.active && (
					<MenuItem onClick={handleConvert}>
						<ListItemText>Convert to Investor</ListItemText>
					</MenuItem>
				)}
				<MenuItem onClick={handleViewInCrm}>
					<ListItemText>View record in CRM</ListItemText>
				</MenuItem>
			</Menu>
		</>
	);
};

FundraisingTableCard.propTypes = {
	title: PropTypes.string.isRequired,
	totalCount: PropTypes.number,
	filterBar: PropTypes.node,
	columnDefs: PropTypes.arrayOf(PropTypes.object),
	getAccountLink: PropTypes.func,
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	loading: PropTypes.bool,
	loadingMore: PropTypes.bool,
	hasMore: PropTypes.bool,
	error: PropTypes.string,
	errorMessage: PropTypes.string,
	sortModel: PropTypes.arrayOf(
		PropTypes.shape({
			field: PropTypes.string,
			sort: PropTypes.oneOf(['asc', 'desc']),
		})
	),
	onSortModelChange: PropTypes.func,
	onRowsScrollEnd: PropTypes.func,
	noRowsMessage: PropTypes.string,
	dataTestId: PropTypes.string,
	cardTestId: PropTypes.string,
	onRefresh: PropTypes.func,
	onMenuAction: PropTypes.func,
};

export default FundraisingTableCard;
